import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import Logo from '../Logo';
import Icon from '../Icon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeChoice } from '../../hooks/useThemeChoice';
import { TabType } from '../../hooks/useNavigation';
import { User } from '../../types/auth';
import './MobileHeader.css';
import { formatPercent } from '../../utils/format';
import { sitePageUrl } from '../../utils/siteLinks';

interface MobileHeaderProps {
  activeTab: TabType | null;
  onTabChange: (tab: TabType) => void;
  onOpenSuggestion: () => void;
  overallProgress: number;
  completedGoalsCount: number;
  totalGoalsCount: number;
  isScrolled: boolean;
  user: User | null;
  onLogout: () => void;
}


const MobileHeader: React.FC<MobileHeaderProps> = ({
  activeTab,
  onTabChange,
  onOpenSuggestion,
  overallProgress,
  completedGoalsCount,
  totalGoalsCount,
  isScrolled,
  user,
  onLogout
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, language } = useTranslation();
  const { resolvedTheme } = useTheme();
  // The switch saves to the account too, like Profile (T-128)
  const chooseTheme = useThemeChoice();
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    menuRef.current?.querySelector<HTMLElement>('button')?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  const runAndClose = (action: () => void) => () => {
    action();
    closeMenu();
  };

  const isDark = resolvedTheme === 'dark';
  const displayName = user?.firstName || user?.username || t('common.user');

  return (
    <>
      <div className={`mobile-header-container ${isScrolled ? 'scrolled' : ''}`}>
        <header className="mobile-header">
          <div className="mobile-header-content">
            <div className="mobile-brand">
              <Logo size={32} className="mobile-logo" />
              <span className="mobile-title">Riselio</span>
            </div>

            <button
              ref={menuButtonRef}
              type="button"
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label={t('common.menu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-side-menu"
            >
              <Icon name="menu" />
            </button>
          </div>

          <div className="mobile-progress-section">
            <span className="mobile-progress-text">
              {completedGoalsCount}/{totalGoalsCount} {t('goals.title')} ({formatPercent(overallProgress, language)})
            </span>
          </div>
        </header>
      </div>

      {isMenuOpen && <div className="mobile-menu-overlay" onClick={closeMenu} />}

      <div
        id="mobile-side-menu"
        ref={menuRef}
        className={`mobile-side-menu ${isMenuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('navigation.accountMenu')}
        aria-hidden={!isMenuOpen}
        inert={!isMenuOpen}
      >
        <div className="mobile-menu-content">
          <div className="mobile-user-section">
            <button
              type="button"
              className="mobile-user-profile-btn"
              onClick={runAndClose(() => { window.location.hash = '#/profile'; })}
            >
              <div className="mobile-user-avatar">
                <span className="mobile-user-initial">
                  {(user?.firstName?.[0] || user?.username?.[0] || 'U').toUpperCase()}
                </span>
              </div>
              <div className="mobile-user-info">
                <span className="mobile-user-greeting">{t('mobileHeader.greeting')}</span>
                <span className="mobile-user-name">{displayName}</span>
                <span className="mobile-profile-hint">{t('navigation.profile')} ›</span>
              </div>
            </button>
          </div>

          <nav className="mobile-nav-section">
            <div className="mobile-nav-list">
              <button
                type="button"
                className={`mobile-nav-item ${activeTab === 'ongoing' ? 'active' : ''}`}
                onClick={runAndClose(() => onTabChange('ongoing'))}
              >
                <Icon name="playCircle" className="mobile-nav-icon" />
                <span className="mobile-nav-text">{t('navigation.ongoing')}</span>
                <Icon name="chevronRight" className="mobile-nav-arrow" />
              </button>
              <button
                type="button"
                className="mobile-nav-item"
                onClick={runAndClose(() => { window.location.hash = '#/settings'; })}
              >
                <Icon name="settings" className="mobile-nav-icon" />
                <span className="mobile-nav-text">{t('navigation.settings')}</span>
                <Icon name="chevronRight" className="mobile-nav-arrow" />
              </button>
              <button
                type="button"
                className="mobile-nav-item"
                onClick={runAndClose(onOpenSuggestion)}
              >
                <Icon name="lightbulb" className="mobile-nav-icon" />
                <span className="mobile-nav-text">{t('navigation.suggestion')}</span>
                <Icon name="chevronRight" className="mobile-nav-arrow" />
              </button>
              <a
                className="mobile-nav-item"
                href={sitePageUrl('contact', language)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
              >
                <Icon name="mail" className="mobile-nav-icon" />
                <span className="mobile-nav-text">{t('navigation.contact')}</span>
                <Icon name="open" className="mobile-nav-arrow" />
              </a>
            </div>

            <div className="mobile-settings">
              <div className="mobile-setting-row">
                <span className="mobile-setting-label">
                  <Icon name={isDark ? 'moon' : 'sun'} className="mobile-nav-icon" />
                  {t('navigation.theme')}
                </span>
                <button
                  type="button"
                  className="mobile-theme-switch"
                  role="switch"
                  aria-checked={isDark}
                  aria-label={t('profile.theme.dark')}
                  onClick={() => chooseTheme(isDark ? 'light' : 'dark')}
                >
                  <span className="mobile-theme-switch__thumb" />
                </button>
              </div>
              <div className="mobile-setting-row">
                <span className="mobile-setting-label">
                  <Icon name="globe" className="mobile-nav-icon" />
                  {t('navigation.language')}
                </span>
                <LanguageSwitcher variant="surface" />
              </div>
            </div>
          </nav>

          <div className="mobile-actions-section">
            <button
              type="button"
              className="mobile-logout-btn"
              onClick={runAndClose(onLogout)}
            >
              <Icon name="logout" className="mobile-action-icon" />
              <span className="mobile-action-text">{t('navigation.logout')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileHeader;
